import { useState, useMemo } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Users,
  Search,
  AlertCircle,
  RefreshCw,
  Loader2,
  Settings,
  User,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import { useLineOAAudience } from './useLineOAAudience'
import { useLineOADashboard } from './useLineOADashboard'
import type { LineFollowerProfile } from '@/lib/api/controlPlaneClient'

// --- Follower Row ---

function FollowerRow({ profile }: { profile: LineFollowerProfile }) {
  const name = profile.display_name || profile.user_id.slice(0, 12) + '...'

  return (
    <Card className="rounded border-0 bg-white">
      <CardContent className="p-4 flex items-center gap-4">
        {/* Avatar */}
        {profile.picture_url ? (
          <img
            src={profile.picture_url}
            alt={name}
            className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-gray-400" />
          </div>
        )}

        {/* Name + status */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{name}</p>
          {profile.status_message && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {profile.status_message}
            </p>
          )}
        </div>

        {/* Language badge */}
        {profile.language && (
          <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
            {profile.language}
          </span>
        )}
      </CardContent>
    </Card>
  )
}

// --- Main Page ---

export function LineOAAudiencePage() {
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig()
  const {
    profiles,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    refetch,
  } = useLineOAAudience({ enabled: !!status?.connected })
  const { data: dashboardData } = useLineOADashboard({
    enabled: !!status?.connected,
  })

  const [searchQuery, setSearchQuery] = useState('')

  // Client-side search filter
  const filteredProfiles = useMemo(() => {
    if (!searchQuery.trim()) return profiles
    const q = searchQuery.toLowerCase()
    return profiles.filter((p) => {
      const name = p.display_name?.toLowerCase() || ''
      return name.includes(q)
    })
  }, [profiles, searchQuery])

  // Loading connection status
  if (isLoadingStatus) {
    return (
      <PageContainer>
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    )
  }

  // Not connected
  if (!status?.connected) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <h2 className="text-2xl font-semibold">Audience</h2>
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <Settings className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="font-medium">Connect LINE Messaging API</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect your LINE Messaging API token in Storefront Settings
                  to browse your audience.
                </p>
              </div>
              <Button asChild>
                <Link to="/storefront/settings">Go to Settings</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    )
  }

  const totalFollowers = dashboardData?.followers

  return (
    <PageContainer>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Audience</h2>
          {totalFollowers != null && (
            <span className="text-sm text-muted-foreground">
              {totalFollowers.toLocaleString()} followers
            </span>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by display name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Search note */}
        {searchQuery.trim() && hasMore && (
          <p className="text-xs text-muted-foreground">
            Showing results from loaded profiles. Load more to search all
            followers.
          </p>
        )}

        {/* Error banner */}
        {isError && (
          <div className="flex items-center gap-3 p-4 rounded bg-red-50 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm flex-1">
              Couldn&apos;t load audience data. Try again.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              className="text-red-700 hover:text-red-800 hover:bg-red-100"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Retry
            </Button>
          </div>
        )}

        {/* Loading skeletons */}
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="rounded border-0 bg-white">
                <CardContent className="p-4 flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && profiles.length === 0 && (
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-8 space-y-4 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <Users className="w-7 h-7 text-gray-400" />
              </div>
              <div>
                <p className="font-medium">No followers yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Share your LINE OA link to start growing your audience. Once
                  people follow your account, they&apos;ll appear here.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No search results */}
        {!isLoading &&
          profiles.length > 0 &&
          filteredProfiles.length === 0 &&
          searchQuery.trim() && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No followers match &ldquo;{searchQuery}&rdquo; in loaded profiles.
            </p>
          )}

        {/* Follower list */}
        {!isLoading && filteredProfiles.length > 0 && (
          <div className="space-y-2">
            {filteredProfiles.map((profile) => (
              <FollowerRow key={profile.user_id} profile={profile} />
            ))}
          </div>
        )}

        {/* Load more */}
        {!isLoading && hasMore && !searchQuery.trim() && (
          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </div>
    </PageContainer>
  )
}
